// Settings → Team & Permissions tab.
//
// Source-of-truth reference: /var/www/kitchenos/portal-standalone.html
//   tab strip:              line 2750 (button data-tab="team")
//   panel container:        line 2759 (#settings-panel-team)
//   renderTeamPanel:        line 8901
//   PERMS catalogue:        line 8558 (18 capabilities)
//   DEFAULT_ROLES seed:     line 8581
//   kosTeamAdd modal:       line 9011
//
// Diverges from source on Steve's 2026-05-21 direction:
//   • 5 roles (Owner / Manager / Sales / Designer / Fitter) instead of
//     source's 3 (Owner-Manager / Designer / Staff).
//   • Real backend persistence via /api/portal/team-members{,/:id,/:id/resend}
//     and /api/portal/role-permissions. Source was localStorage-only.
//   • Brevo invite email with 7-day TTL accept link.
//
// Backend wiring
// --------------
//   GET    /api/portal/team-members              list (status: invited|active|suspended|expired)
//   POST   /api/portal/team-members              invite { name, email, mobile?, role }
//   PUT    /api/portal/team-members/:id          { role?, active? }
//   DELETE /api/portal/team-members/:id          remove
//   POST   /api/portal/team-members/:id/resend   resend invite email
//   GET    /api/portal/role-permissions          full matrix
//   PUT    /api/portal/role-permissions          { role, perm, enabled }
//   POST   /api/portal/role-permissions/reset    defaults

import { ApiError } from "@tkos/api-client";
import { confirmDialog, promptDialog, iconMarkup } from "@tkos/shared-ui";
import type { UserStatus } from "@tkos/tkos-types";

import type { PageContext } from "../../../shell/types";
import type { SettingsTab, TabCleanup } from "../types";

import "./team-permissions.css";

// ── Types ────────────────────────────────────────────────────────────────────

// 4-role spec (Steve 2026-05-21 merge): designer and salesperson are
// typically the same person in kitchen retail, so the two seats merged
// into one combined `designer-sales` row. Mirrors server ROLE_KEYS.
const PORTAL_ROLES = ["owner", "manager", "designer-sales", "fitter"] as const;
type PortalRole = (typeof PORTAL_ROLES)[number];

/** Roles that the invite + role-change UI may assign. Owner is fixed —
 *  ownership transfer is out of scope for this surface. */
const ASSIGNABLE_ROLES: readonly PortalRole[] = ["manager", "designer-sales", "fitter"];

interface RoleSpec {
  key: PortalRole;
  label: string;
  desc: string;
  color: string;
}

const ROLES: readonly RoleSpec[] = [
  {
    key: "owner",
    label: "Owner",
    desc: "Full access to everything in the portal — billing, financials, team, settings. Cannot be removed.",
    color: "#e58200",
  },
  {
    key: "manager",
    label: "Manager",
    desc: "Day-to-day management of the whole business — team, customers, projects, suppliers. Excludes billing.",
    color: "#22c55e",
  },
  {
    key: "designer-sales",
    label: "Designer / Sales",
    desc: "End-to-end customer ownership: CRM, quotes, messages, calendar, bookings, designs and project handover. No financials, no team management, no platform settings.",
    color: "#a855f7",
  },
  {
    key: "fitter",
    label: "Fitter",
    desc: "Assigned jobs and the delivery schedule only. Fitter portal lands later — role label reserved now.",
    color: "#0ea5e9",
  },
];

const ROLE_BY_KEY: Record<PortalRole, RoleSpec> = ROLES.reduce(
  (acc, r) => {
    acc[r.key] = r;
    return acc;
  },
  {} as Record<PortalRole, RoleSpec>,
);

/** Server can return 'invited' | 'active' | 'suspended' | 'expired'; legacy
 *  UserStatus is a narrower {active|invited|suspended} — extend locally. */
type MemberStatus = UserStatus | "expired";

const STATUS_SPECS: Record<MemberStatus, { label: string; cls: string }> = {
  active:    { label: "Active",      cls: "set-team-pill--active" },
  invited:   { label: "Invite sent", cls: "set-team-pill--invited" },
  expired:   { label: "Invite expired", cls: "set-team-pill--expired" },
  suspended: { label: "Suspended",   cls: "set-team-pill--suspended" },
};

interface TeamMember {
  id: string;
  name: string;
  email: string;
  mobile: string;
  role: PortalRole;
  status: MemberStatus;
  owner: boolean;
  invitedAt: string | null;
  acceptedAt: string | null;
}

interface ServerMember {
  id?: string;
  name?: string;
  email?: string;
  mobile?: string;
  role?: string;
  status?: string;
  owner?: boolean;
  invitedAt?: string | null;
  acceptedAt?: string | null;
}

interface TeamMembersResponse {
  ok?: boolean;
  members?: ServerMember[];
}
interface InviteResponse {
  ok?: boolean;
  member?: ServerMember;
  emailSent?: boolean;
  error?: string;
}
interface UpdateMemberResponse {
  ok?: boolean;
  member?: ServerMember;
  error?: string;
}

// ── Permission catalogue + matrix ────────────────────────────────────────────

interface PermDef {
  k: string;
  n: string;
  d: string;
}

/** Default catalogue — overwritten on first matrix fetch (the server is
 *  authoritative). We keep a local copy so the first render before the
 *  fetch completes still shows accurate rows. */
const DEFAULT_PERMS: readonly PermDef[] = [
  { k: "dashboard",         n: "Dashboard",            d: "View the main overview dashboard" },
  { k: "messages",          n: "Messages",             d: "Customer + supplier message threads (platform-only)" },
  { k: "calendar",          n: "Calendar",             d: "View and manage events, installs and surveys" },
  { k: "submissions",       n: "Submissions / Leads",  d: "Web form leads and enquiries" },
  { k: "crm",               n: "CRM & Customers",      d: "Customer records, pipeline and contacts" },
  { k: "designer-projects", n: "Designer projects",    d: "Their own assigned design projects" },
  { k: "operations",        n: "Operations",           d: "Installs, site surveys and snags" },
  { k: "delivery",          n: "Delivery tracking",    d: "Track and update deliveries" },
  { k: "bookings",          n: "Bookings",             d: "Showroom appointments and consultations" },
  { k: "radio",             n: "Radio",                d: "Top-bar radio chip" },
  { k: "browser",           n: "In-OS Browser",        d: "Built-in supplier browser" },
  { k: "ai-tools",          n: "AI Tools",             d: "Kit, Social Media AI and other assistants" },
  { k: "financials",        n: "Financials & Revenue", d: "Revenue widgets, invoice values, P&L" },
  { k: "analytics",         n: "Business Analytics",   d: "Reports, conversions and forecasting" },
  { k: "supplier-pricing",  n: "Supplier pricing",     d: "Trade prices, rebates and discount deals" },
  { k: "suppliers",         n: "Supplier directory",   d: "Supplier contact details and catalogues" },
  { k: "team-management",   n: "Team management",      d: "Add staff and assign roles" },
  { k: "settings",          n: "Business settings",    d: "Branding, integrations and global settings" },
];

type RoleMatrix = Record<PortalRole, Record<string, boolean>>;

interface RolePermsResponse {
  ok?: boolean;
  roles?: Record<string, Record<string, boolean>>;
  perms?: PermDef[];
}

const PREVIEW_KEY = "tkos.settings.preview-role";

function readPreviewRole(): PortalRole {
  try {
    const v = localStorage.getItem(PREVIEW_KEY);
    if (v && (PORTAL_ROLES as readonly string[]).includes(v)) return v as PortalRole;
  } catch { /* ignore */ }
  return "owner";
}
function writePreviewRole(r: PortalRole): void {
  try { localStorage.setItem(PREVIEW_KEY, r); } catch { /* ignore */ }
}

// ── Coercers ─────────────────────────────────────────────────────────────────

function coerceRole(raw: unknown): PortalRole {
  if (typeof raw !== "string") return "designer-sales";
  const lc = raw.trim().toLowerCase();
  if ((PORTAL_ROLES as readonly string[]).includes(lc)) return lc as PortalRole;
  // Map legacy / external values defensively so existing seeded users
  // don't disappear. The 5-role pre-2026-05-21 split into separate
  // `sales` + `designer` rows folds into the merged `designer-sales` seat.
  if (lc === "admin") return "owner";
  if (lc === "staff") return "designer-sales";
  if (lc === "sales") return "designer-sales";
  if (lc === "designer") return "designer-sales";
  if (lc === "installer") return "fitter";
  if (lc === "sales_manager") return "manager";
  return "designer-sales";
}

function coerceStatus(raw: unknown): MemberStatus {
  if (typeof raw !== "string") return "active";
  const lc = raw.trim().toLowerCase();
  if (lc === "active" || lc === "invited" || lc === "suspended" || lc === "expired") {
    return lc as MemberStatus;
  }
  return "active";
}

function normaliseMember(m: ServerMember): TeamMember {
  return {
    id: String(m.id ?? `tmp-${Math.random().toString(36).slice(2)}`),
    name: String(m.name ?? "").trim() || "Unnamed",
    email: String(m.email ?? "").trim(),
    mobile: String(m.mobile ?? "").trim(),
    role: coerceRole(m.role),
    status: coerceStatus(m.status),
    owner: Boolean(m.owner),
    invitedAt: m.invitedAt ?? null,
    acceptedAt: m.acceptedAt ?? null,
  };
}

// ── Utilities ────────────────────────────────────────────────────────────────

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?"
  );
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

// ── Backend calls ────────────────────────────────────────────────────────────

async function loadMembers(
  ctx: PageContext,
  signal: AbortSignal,
): Promise<TeamMember[]> {
  const resp = await ctx.api<TeamMembersResponse>("/portal/team-members", { signal });
  const list = Array.isArray(resp?.members) ? resp.members : [];
  return list.map(normaliseMember);
}

async function loadMatrix(
  ctx: PageContext,
  signal: AbortSignal,
): Promise<{ matrix: RoleMatrix; perms: readonly PermDef[] }> {
  const resp = await ctx.api<RolePermsResponse>("/portal/role-permissions", { signal });
  const raw = resp?.roles ?? {};
  const matrix = {} as RoleMatrix;
  for (const r of PORTAL_ROLES) {
    const row = raw[r] ?? {};
    const out: Record<string, boolean> = {};
    for (const p of DEFAULT_PERMS) out[p.k] = !!row[p.k];
    matrix[r] = out;
  }
  // Owner is always fully on regardless of payload.
  for (const p of DEFAULT_PERMS) matrix.owner[p.k] = true;
  const perms = Array.isArray(resp?.perms) && resp.perms.length > 0 ? resp.perms : DEFAULT_PERMS;
  return { matrix, perms };
}

// ── Invite modal (built via createModal-style overlay div) ───────────────────

interface InviteModalRefs {
  overlay: HTMLDivElement;
  form: HTMLFormElement;
  nameInput: HTMLInputElement;
  emailInput: HTMLInputElement;
  mobileInput: HTMLInputElement;
  roleSelect: HTMLSelectElement;
  submitBtn: HTMLButtonElement;
  cancelBtn: HTMLButtonElement;
  errEl: HTMLDivElement;
}

function buildInviteModal(): InviteModalRefs {
  const overlay = document.createElement("div");
  overlay.className = "set-team-modal-overlay";
  overlay.setAttribute("role", "presentation");
  overlay.hidden = true;
  overlay.innerHTML = `
    <div class="set-team-modal" role="dialog" aria-modal="true" aria-labelledby="set-team-modal-title">
      <header class="set-team-modal__head">
        <h2 class="set-team-modal__title" id="set-team-modal-title">Invite team member</h2>
      </header>
      <form class="set-team-modal__form" novalidate>
        <div class="set-team-modal__body">
          <div class="set-field">
            <label for="set-team-invite-name">Full name</label>
            <input id="set-team-invite-name" class="set-input" type="text" required
              placeholder="Team member's full name" autocomplete="off" data-role="name">
          </div>
          <div class="set-field">
            <label for="set-team-invite-email">Work email</label>
            <input id="set-team-invite-email" class="set-input" type="email" required
              placeholder="sarah@example.co.uk" autocomplete="off" data-role="email">
          </div>
          <div class="set-field">
            <label for="set-team-invite-mobile">Mobile number <span class="set-team-modal__hint">for click-to-connect calls</span></label>
            <input id="set-team-invite-mobile" class="set-input" type="tel"
              placeholder="+44 7700 900123" autocomplete="off" data-role="mobile">
          </div>
          <div class="set-field">
            <label for="set-team-invite-role">Role</label>
            <select id="set-team-invite-role" class="set-select" data-role="role">
              ${ASSIGNABLE_ROLES
                .map((rk) => {
                  const r = ROLE_BY_KEY[rk];
                  return `<option value="${r.key}"${r.key === "designer-sales" ? " selected" : ""}>${escapeHtml(r.label)} — ${escapeHtml(r.desc)}</option>`;
                })
                .join("")}
            </select>
            <div class="set-help">You can change the role later from the team list.</div>
          </div>
          <div class="set-team-modal__error" data-role="error" aria-live="polite" hidden></div>
        </div>
        <footer class="set-team-modal__footer">
          <button type="button" class="set-btn-secondary set-team-btn" data-role="cancel">Cancel</button>
          <button type="submit" class="set-btn" data-role="submit">Send invite</button>
        </footer>
      </form>
    </div>
  `;

  return {
    overlay,
    form: overlay.querySelector<HTMLFormElement>(".set-team-modal__form")!,
    nameInput: overlay.querySelector<HTMLInputElement>("[data-role='name']")!,
    emailInput: overlay.querySelector<HTMLInputElement>("[data-role='email']")!,
    mobileInput: overlay.querySelector<HTMLInputElement>("[data-role='mobile']")!,
    roleSelect: overlay.querySelector<HTMLSelectElement>("[data-role='role']")!,
    submitBtn: overlay.querySelector<HTMLButtonElement>("[data-role='submit']")!,
    cancelBtn: overlay.querySelector<HTMLButtonElement>("[data-role='cancel']")!,
    errEl: overlay.querySelector<HTMLDivElement>("[data-role='error']")!,
  };
}

// ── Rendering ────────────────────────────────────────────────────────────────

function renderShell(container: HTMLElement): {
  bannerEl: HTMLElement;
  listEl: HTMLElement;
  rolesEl: HTMLElement;
  inviteBtn: HTMLButtonElement;
  matrixEl: HTMLElement;
  matrixFootEl: HTMLElement;
} {
  container.innerHTML = `
    <section class="set-team-root" aria-label="Team and permissions">
      <div class="set-team-banner" data-role="banner" hidden></div>

      <div class="set-team-head">
        <div>
          <h2 class="set-section-title">Team members</h2>
          <p class="set-help">Invite the people who run your business with you. Each person gets their own login and only sees what their role allows.</p>
        </div>
        <button class="set-btn" type="button" data-role="invite">
          <span aria-hidden="true">+</span>&nbsp;Invite team member
        </button>
      </div>

      <div class="set-team-table" role="table" aria-label="Team members">
        <div class="set-team-row set-team-row--head" role="row">
          <div class="set-team-cell set-team-cell--name" role="columnheader">Name &amp; Email</div>
          <div class="set-team-cell set-team-cell--status" role="columnheader">Status</div>
          <div class="set-team-cell set-team-cell--role" role="columnheader">Role</div>
          <div class="set-team-cell set-team-cell--actions" role="columnheader">Actions</div>
        </div>
        <div class="set-team-list" data-role="list" role="rowgroup">
          <div class="set-loading">Loading team…</div>
        </div>
      </div>

      <section class="set-section">
        <h2 class="set-section-title">Roles</h2>
        <p class="set-help">Five default roles ship with The Kitchen OS. The Owner can fine-tune what each role can see and do via the matrix below.</p>
        <div class="set-team-roles" data-role="roles"></div>
      </section>

      <section class="set-section">
        <h2 class="set-section-title">Permissions matrix</h2>
        <p class="set-help">Toggle individual permissions per role. Owner has full access at all times (column locked). Changes save instantly.</p>
        <div class="set-team-matrix-wrap">
          <div class="set-team-matrix" data-role="matrix">
            <div class="set-loading">Loading permissions…</div>
          </div>
        </div>
        <div class="set-team-matrix-foot" data-role="matrix-foot"></div>
      </section>
    </section>
  `;

  return {
    bannerEl:     container.querySelector<HTMLElement>("[data-role='banner']")!,
    listEl:       container.querySelector<HTMLElement>("[data-role='list']")!,
    rolesEl:      container.querySelector<HTMLElement>("[data-role='roles']")!,
    inviteBtn:    container.querySelector<HTMLButtonElement>("[data-role='invite']")!,
    matrixEl:     container.querySelector<HTMLElement>("[data-role='matrix']")!,
    matrixFootEl: container.querySelector<HTMLElement>("[data-role='matrix-foot']")!,
  };
}

function renderRoleCards(host: HTMLElement): void {
  host.innerHTML = ROLES.map(
    (r) => `
      <article class="set-team-role-card" style="--set-team-role-color:${r.color}">
        <div class="set-team-role-name">
          <span class="set-team-role-dot" aria-hidden="true"></span>
          ${escapeHtml(r.label)}
        </div>
        <div class="set-team-role-desc">${escapeHtml(r.desc)}</div>
      </article>
    `,
  ).join("");
}

// Mirror of the server-side EMAIL_RX (portal_contacts.js) so we validate
// before the round-trip and give instant feedback.
const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// `meId` / `meIsOwner` identify the signed-in user's OWN row so we render the
// self-service "Edit" email affordance there and nowhere else. Legacy v2
// sessions carry no userId — fall back to the owner row for them.
function renderMembers(
  host: HTMLElement,
  members: TeamMember[],
  meId = "",
  meIsOwner = false,
): void {
  if (members.length === 0) {
    host.innerHTML = `<div class="set-empty">No team members yet — invite your first.</div>`;
    return;
  }

  host.innerHTML = members
    .map((m) => {
      const isMe = meId ? m.id === meId : meIsOwner && m.owner;
      const status = STATUS_SPECS[m.status] ?? STATUS_SPECS.active;
      const role = ROLE_BY_KEY[m.role];
      const avatarBg = role.color;

      let actions = "";
      if (m.status === "invited" || m.status === "expired") {
        actions += `<button type="button" class="set-btn-secondary set-team-btn" data-action="resend" data-id="${escapeHtml(m.id)}">Resend invite</button>`;
      }
      if (!m.owner) {
        actions += `<button type="button" class="set-btn-secondary set-team-btn set-team-btn--danger" data-action="remove" data-id="${escapeHtml(m.id)}">Remove</button>`;
      }

      const roleOptions = ROLES.map(
        (r) =>
          `<option value="${r.key}"${r.key === m.role ? " selected" : ""}${r.key === "owner" && !m.owner ? " disabled" : ""}>${escapeHtml(r.label)}</option>`,
      ).join("");

      const ownerPill = m.owner
        ? `<span class="set-team-pill set-team-pill--owner">Owner</span>`
        : "";

      return `
        <div class="set-team-row" role="row" data-member-id="${escapeHtml(m.id)}">
          <div class="set-team-cell set-team-cell--name" role="cell">
            <span class="set-team-avatar" aria-hidden="true" style="background:${avatarBg}">
              ${escapeHtml(initialsOf(m.name))}
            </span>
            <div class="set-team-name-block">
              <div class="set-team-name">
                ${escapeHtml(m.name)} ${ownerPill}
              </div>
              <div class="set-team-email">
                ${escapeHtml(m.email)}
                ${isMe ? `<button type="button" class="set-team-email-edit" data-action="edit-email" data-id="${escapeHtml(m.id)}" aria-label="Change your sign-in email">Edit</button>` : ""}
              </div>
            </div>
          </div>
          <div class="set-team-cell set-team-cell--status" role="cell">
            <span class="set-team-pill ${status.cls}">${escapeHtml(status.label)}</span>
          </div>
          <div class="set-team-cell set-team-cell--role" role="cell">
            <select class="set-select set-team-role-select"
              data-action="set-role" data-id="${escapeHtml(m.id)}"
              ${m.owner ? "disabled" : ""}
              aria-label="Role for ${escapeHtml(m.name)}">
              ${roleOptions}
            </select>
          </div>
          <div class="set-team-cell set-team-cell--actions" role="cell">
            ${actions}
          </div>
        </div>
      `;
    })
    .join("");
}

function renderMatrix(
  host: HTMLElement,
  perms: readonly PermDef[],
  matrix: RoleMatrix,
): void {
  const head = ROLES.map(
    (r) =>
      `<th class="set-perm-head" style="color:${r.color}">${escapeHtml(r.label)}</th>`,
  ).join("");

  const rows = perms
    .map((p) => {
      const cells = ROLES.map((r) => {
        const checked = !!(matrix[r.key] && matrix[r.key][p.k]);
        const locked = r.key === "owner";
        return `
          <td class="set-perm-cell">
            <label class="set-perm-toggle">
              <input
                type="checkbox"
                ${checked ? "checked" : ""}
                ${locked ? "disabled" : ""}
                data-role="perm-toggle"
                data-role-key="${escapeHtml(r.key)}"
                data-perm-key="${escapeHtml(p.k)}"
                aria-label="${escapeHtml(r.label)} can ${escapeHtml(p.n)}">
              <span class="set-perm-toggle__track" aria-hidden="true"></span>
              <span class="set-perm-toggle__thumb" aria-hidden="true"></span>
            </label>
          </td>
        `;
      }).join("");
      return `
        <tr>
          <td class="set-perm-name-cell">
            <div class="set-perm-name">${escapeHtml(p.n)}</div>
            <div class="set-perm-desc">${escapeHtml(p.d)}</div>
          </td>
          ${cells}
        </tr>
      `;
    })
    .join("");

  host.innerHTML = `
    <table class="set-perm-matrix" role="table" aria-label="Role permissions matrix">
      <thead>
        <tr>
          <th class="set-perm-head set-perm-head--label">Permission</th>
          ${head}
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  `;
}

function renderMatrixFoot(host: HTMLElement, currentPreview: PortalRole): void {
  const currentRole = ROLE_BY_KEY[currentPreview];
  const opts = ROLES.map(
    (r) =>
      `<option value="${r.key}"${r.key === currentPreview ? " selected" : ""}>Preview as ${escapeHtml(r.label)}</option>`,
  ).join("");
  host.innerHTML = `
    <div class="set-team-matrix-foot__msg">
      Currently previewing portal as
      <strong style="color:${currentRole.color}">${escapeHtml(currentRole.label)}</strong>.
    </div>
    <div class="set-team-matrix-foot__actions">
      <select class="set-select" data-role="preview-select" aria-label="Preview portal as">
        ${opts}
      </select>
      <button type="button" class="set-btn-secondary set-team-btn" data-role="reset">Reset roles to default</button>
    </div>
  `;
}

// ── Tab ──────────────────────────────────────────────────────────────────────

const tab: SettingsTab = {
  id: "team-permissions",
  label: "Team & Permissions",
  icon: iconMarkup("team", { size: "sm" }),

  mount(container: HTMLElement, ctx: PageContext): TabCleanup {
    const ac = new AbortController();
    let members: TeamMember[] = [];
    let perms: readonly PermDef[] = DEFAULT_PERMS;
    let matrix: RoleMatrix = ROLES.reduce((acc, r) => {
      const row: Record<string, boolean> = {};
      for (const p of DEFAULT_PERMS) row[p.k] = r.key === "owner";
      acc[r.key] = row;
      return acc;
    }, {} as RoleMatrix);
    let previewRole: PortalRole = readPreviewRole();
    let disposed = false;

    const { bannerEl, listEl, rolesEl, inviteBtn, matrixEl, matrixFootEl } = renderShell(container);
    renderRoleCards(rolesEl);
    renderMatrixFoot(matrixFootEl, previewRole);

    // Invite modal — appended inside the tab container so cleanup is automatic.
    const modal = buildInviteModal();
    container.appendChild(modal.overlay);

    function setBanner(msg: string | null, kind: "info" | "error" = "info"): void {
      if (!msg) {
        bannerEl.hidden = true;
        bannerEl.textContent = "";
        bannerEl.classList.remove("set-team-banner--error");
        return;
      }
      bannerEl.hidden = false;
      bannerEl.textContent = msg;
      bannerEl.classList.toggle("set-team-banner--error", kind === "error");
    }

    const meId = ctx.user?.userId ?? "";
    const meIsOwner = (ctx.user?.role ?? "").toLowerCase() === "owner";
    function rerenderMembers(): void {
      renderMembers(listEl, members, meId, meIsOwner);
    }
    function rerenderMatrix(): void {
      renderMatrix(matrixEl, perms, matrix);
    }
    function rerenderMatrixFoot(): void {
      renderMatrixFoot(matrixFootEl, previewRole);
    }

    function setModalError(msg: string | null): void {
      if (!msg) {
        modal.errEl.hidden = true;
        modal.errEl.textContent = "";
      } else {
        modal.errEl.hidden = false;
        modal.errEl.textContent = msg;
      }
    }

    function openInviteModal(): void {
      modal.form.reset();
      modal.roleSelect.value = "designer-sales";
      setModalError(null);
      modal.overlay.hidden = false;
      window.requestAnimationFrame(() => modal.nameInput.focus());
    }
    function closeInviteModal(): void {
      modal.overlay.hidden = true;
    }

    // ── Event handlers ────────────────────────────────────────────────────

    const onInviteClick = (): void => openInviteModal();
    inviteBtn.addEventListener("click", onInviteClick);

    const onCancelClick = (): void => closeInviteModal();
    modal.cancelBtn.addEventListener("click", onCancelClick);

    const onOverlayClick = (e: MouseEvent): void => {
      if (e.target === modal.overlay) closeInviteModal();
    };
    modal.overlay.addEventListener("click", onOverlayClick);

    const onModalKeydown = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !modal.overlay.hidden) {
        e.preventDefault();
        closeInviteModal();
      }
    };
    document.addEventListener("keydown", onModalKeydown);

    const onInviteSubmit = async (e: SubmitEvent): Promise<void> => {
      e.preventDefault();
      setModalError(null);
      const name   = modal.nameInput.value.trim();
      const email  = modal.emailInput.value.trim();
      const mobile = modal.mobileInput.value.trim();
      const role   = coerceRole(modal.roleSelect.value);
      if (!name)  { setModalError("Please enter a full name."); modal.nameInput.focus(); return; }
      if (!email) { setModalError("Please enter a work email."); modal.emailInput.focus(); return; }

      modal.submitBtn.disabled = true;
      const originalLabel = modal.submitBtn.textContent;
      modal.submitBtn.textContent = "Sending…";
      try {
        const resp = await ctx.api<InviteResponse>("/portal/team-members", {
          method: "POST",
          signal: ac.signal,
          body: JSON.stringify({ name, email, mobile, role }),
        });
        if (disposed) return;
        if (!resp?.ok || !resp.member) {
          setModalError(resp?.error || "Could not send the invite.");
          return;
        }
        const newMember = normaliseMember(resp.member);
        members = [...members.filter((m) => m.id !== newMember.id), newMember];
        rerenderMembers();
        closeInviteModal();
        ctx.showToast(
          resp.emailSent
            ? `Invite sent to ${email}`
            : `Invite created for ${email} — email send pending`,
          "success",
        );
      } catch (err) {
        if (isAbortError(err)) return;
        let msg = "Couldn't send the invite.";
        if (err instanceof ApiError) {
          msg = err.message || `HTTP ${err.status}`;
          // ApiError messages from this backend tend to be JSON — surface
          // the server's `error` slug verbatim when present.
          try {
            const parsed = JSON.parse(err.message);
            if (parsed && typeof parsed.error === "string") {
              msg = humaniseError(parsed.error);
            }
          } catch { /* not JSON */ }
        } else if (err instanceof Error) {
          msg = err.message;
        }
        setModalError(msg);
      } finally {
        if (!disposed) {
          modal.submitBtn.disabled = false;
          modal.submitBtn.textContent = originalLabel;
        }
      }
    };
    modal.form.addEventListener("submit", onInviteSubmit);

    function humaniseError(slug: string): string {
      switch (slug) {
        case "email_in_use":     return "That email is already on the team.";
        case "invalid_email":    return "Please enter a valid email address.";
        case "invalid_role":     return "That role can't be assigned via invite.";
        case "name_required":    return "Please enter a full name.";
        case "owner_locked":     return "The owner role can't be changed from here.";
        case "already_accepted": return "That invite has already been accepted.";
        case "not_found":        return "That team member couldn't be found.";
        case "expired":          return "That invite has expired.";
        default:                 return "Something went wrong. Please try again.";
      }
    }

    async function patchMember(
      id: string,
      patch: { role?: PortalRole; active?: boolean },
    ): Promise<TeamMember | null> {
      try {
        const resp = await ctx.api<UpdateMemberResponse>(
          `/portal/team-members/${encodeURIComponent(id)}`,
          {
            method: "PUT",
            signal: ac.signal,
            body: JSON.stringify(patch),
          },
        );
        if (!resp?.ok || !resp.member) return null;
        return normaliseMember(resp.member);
      } catch (err) {
        if (isAbortError(err)) return null;
        ctx.showToast(
          err instanceof ApiError
            ? `Couldn't update member (HTTP ${err.status}).`
            : "Couldn't update member.",
          "error",
        );
        return null;
      }
    }

    const onListClick = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const btn = target.closest<HTMLButtonElement>("[data-action]");
      if (!btn) return;
      const action = btn.dataset.action;
      const id = btn.dataset.id;
      if (!action || !id) return;
      const member = members.find((m) => m.id === id);
      if (!member) return;

      switch (action) {
        case "edit-email":
          void (async () => {
            const current = member.email || "";
            const entered = await promptDialog(
              "Enter the new email address you'll use to sign in.",
              {
                title: "Change sign-in email",
                defaultValue: current,
                placeholder: "you@example.com",
                confirmLabel: "Continue",
              },
            );
            if (entered == null) return; // cancelled
            const next = entered.trim().toLowerCase();
            if (!next || next === current.trim().toLowerCase()) return;
            if (!EMAIL_RX.test(next)) {
              ctx.showToast("Please enter a valid email address.", "error");
              return;
            }
            const ok = await confirmDialog(
              `Change your sign-in email to ${next}? You'll use this address to log in from now on. We'll email both your old and new address to confirm.`,
              { title: "Confirm email change", confirmLabel: "Change email" },
            );
            if (!ok) return;
            try {
              const resp = await ctx.api<{ ok?: boolean; email?: string; error?: string }>(
                "/portal/account/email",
                { method: "PATCH", signal: ac.signal, body: JSON.stringify({ email: next }) },
              );
              if (disposed) return;
              if (!resp?.ok) {
                ctx.showToast(humaniseError(resp?.error ?? ""), "error");
                return;
              }
              const saved = resp.email ?? next;
              members = members.map((m) => (m.id === id ? { ...m, email: saved } : m));
              rerenderMembers();
              ctx.showToast(
                "Sign-in email updated — check both inboxes for confirmation.",
                "success",
              );
            } catch (err) {
              if (isAbortError(err)) return;
              let msg = "Couldn't update your email.";
              if (err instanceof ApiError) {
                msg = `Couldn't update your email (HTTP ${err.status}).`;
                // The backend's error body arrives as a JSON string in
                // err.message — surface its `error` slug verbatim.
                try {
                  const parsed = JSON.parse(err.message);
                  if (parsed && typeof parsed.error === "string") {
                    msg = humaniseError(parsed.error);
                  }
                } catch { /* not JSON */ }
              }
              ctx.showToast(msg, "error");
            }
          })();
          break;
        case "resend":
          void (async () => {
            const wasLabel = btn.textContent;
            btn.disabled = true;
            btn.textContent = "Sending…";
            try {
              const resp = await ctx.api<{ ok?: boolean; emailSent?: boolean }>(
                `/portal/team-members/${encodeURIComponent(id)}/resend`,
                { method: "POST", signal: ac.signal, body: JSON.stringify({}) },
              );
              if (resp?.ok) {
                ctx.showToast(
                  resp.emailSent
                    ? `Invite re-sent to ${member.email}`
                    : `Invite refreshed for ${member.email}`,
                  "success",
                );
              } else {
                ctx.showToast("Couldn't resend the invite.", "error");
              }
            } catch (err) {
              if (isAbortError(err)) return;
              ctx.showToast(
                err instanceof ApiError ? `HTTP ${err.status}` : "Couldn't resend the invite.",
                "error",
              );
            } finally {
              if (!disposed) {
                btn.disabled = false;
                btn.textContent = wasLabel;
              }
            }
          })();
          break;
        case "remove":
          void (async () => {
            if (
              !(await confirmDialog(`Remove ${member.name} from the team?`, {
                title: "Remove team member",
                confirmLabel: "Remove",
                destructive: true,
              }))
            ) return;
            try {
              await ctx.api(`/portal/team-members/${encodeURIComponent(id)}`, {
                method: "DELETE",
                signal: ac.signal,
              });
              members = members.filter((m) => m.id !== id);
              rerenderMembers();
              ctx.showToast(`${member.name} removed`, "info");
            } catch (err) {
              if (isAbortError(err)) return;
              ctx.showToast(
                err instanceof ApiError ? `HTTP ${err.status}` : "Couldn't remove the member.",
                "error",
              );
            }
          })();
          break;
      }
    };
    listEl.addEventListener("click", onListClick);

    const onRoleChange = (e: Event): void => {
      const target = e.target as HTMLElement | null;
      if (!(target instanceof HTMLSelectElement)) return;
      if (target.dataset.action !== "set-role") return;
      const id = target.dataset.id;
      if (!id) return;
      const member = members.find((m) => m.id === id);
      if (!member) return;
      const nextRole = coerceRole(target.value);
      if (nextRole === member.role) return;
      void (async () => {
        const updated = await patchMember(id, { role: nextRole });
        if (!updated) {
          // Revert the select to the old value on failure.
          target.value = member.role;
          return;
        }
        members = members.map((m) => (m.id === id ? updated : m));
        rerenderMembers();
        ctx.showToast(`${updated.name} is now ${ROLE_BY_KEY[updated.role].label}`, "success");
      })();
    };
    listEl.addEventListener("change", onRoleChange);

    // ── Matrix interactions ───────────────────────────────────────────────

    const onMatrixChange = (e: Event): void => {
      const target = e.target as HTMLElement | null;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.dataset.role !== "perm-toggle") return;
      const role = target.dataset.roleKey as PortalRole | undefined;
      const perm = target.dataset.permKey;
      if (!role || !perm) return;
      // Owner column is disabled in markup, but double-check.
      if (role === "owner") {
        target.checked = true;
        return;
      }
      const prev = !!(matrix[role] && matrix[role][perm]);
      const next = target.checked;
      matrix[role][perm] = next; // optimistic
      // Tell the shell the matrix changed so role-gating re-evaluates
      // immediately — gives the user instant feedback even if they're
      // toggling the perm of the currently-previewed role.
      document.dispatchEvent(
        new CustomEvent("tkos:role-matrix-changed", { detail: { roles: matrix } }),
      );
      void (async () => {
        try {
          await ctx.api<{ ok?: boolean }>("/portal/role-permissions", {
            method: "PUT",
            signal: ac.signal,
            body: JSON.stringify({ role, perm, enabled: next }),
          });
        } catch (err) {
          if (isAbortError(err)) return;
          // Revert + re-broadcast so the shell undoes its own gate flip.
          matrix[role][perm] = prev;
          target.checked = prev;
          document.dispatchEvent(
            new CustomEvent("tkos:role-matrix-changed", { detail: { roles: matrix } }),
          );
          ctx.showToast(
            err instanceof ApiError ? `HTTP ${err.status}` : "Couldn't save permission.",
            "error",
          );
        }
      })();
    };
    matrixEl.addEventListener("change", onMatrixChange);

    const onMatrixFootChange = (e: Event): void => {
      const target = e.target as HTMLElement | null;
      if (!(target instanceof HTMLSelectElement)) return;
      if (target.dataset.role !== "preview-select") return;
      previewRole = coerceRole(target.value);
      writePreviewRole(previewRole);
      rerenderMatrixFoot();
      // Tell the shell to re-apply role-gating site-wide. shell.ts owns
      // the role-gate module and listens for this event so we don't have
      // to import role-gate from inside a settings tab (which would bind
      // a settings change to a chrome module).
      document.dispatchEvent(
        new CustomEvent("tkos:role-changed", { detail: { role: previewRole } }),
      );
    };
    matrixFootEl.addEventListener("change", onMatrixFootChange);

    const onMatrixFootClick = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const btn = target.closest<HTMLButtonElement>("[data-role='reset']");
      if (!btn) return;
      void (async () => {
        if (
          !(await confirmDialog(
            "Reset all role permissions to their defaults? Custom toggles will be lost.",
            { title: "Reset roles", confirmLabel: "Reset", destructive: true },
          ))
        ) return;
        try {
          const resp = await ctx.api<RolePermsResponse>("/portal/role-permissions/reset", {
            method: "POST",
            signal: ac.signal,
          });
          const newMatrix = {} as RoleMatrix;
          for (const r of PORTAL_ROLES) {
            const row = (resp?.roles && resp.roles[r]) ?? {};
            const out: Record<string, boolean> = {};
            for (const p of perms) out[p.k] = !!row[p.k];
            newMatrix[r] = out;
          }
          for (const p of perms) newMatrix.owner[p.k] = true;
          matrix = newMatrix;
          rerenderMatrix();
          document.dispatchEvent(
            new CustomEvent("tkos:role-matrix-changed", { detail: { roles: matrix } }),
          );
          ctx.showToast("Permissions reset to defaults.", "success");
        } catch (err) {
          if (isAbortError(err)) return;
          ctx.showToast(
            err instanceof ApiError ? `HTTP ${err.status}` : "Couldn't reset permissions.",
            "error",
          );
        }
      })();
    };
    matrixFootEl.addEventListener("click", onMatrixFootClick);

    // ── Initial data load ─────────────────────────────────────────────────

    async function load(): Promise<void> {
      try {
        const [m, mat] = await Promise.all([
          loadMembers(ctx, ac.signal),
          loadMatrix(ctx, ac.signal),
        ]);
        if (disposed || ac.signal.aborted) return;
        members = m;
        perms = mat.perms;
        matrix = mat.matrix;
        // Keep the shell's role-gate matrix in lock-step with this tab.
        // If the user toggles a perm here and then navigates away, the
        // shell already had the same payload — no second fetch needed.
        document.dispatchEvent(
          new CustomEvent("tkos:role-matrix-changed", { detail: { roles: matrix } }),
        );
        setBanner(null);
      } catch (err) {
        if (isAbortError(err) || ac.signal.aborted) return;
        const msg = err instanceof ApiError
          ? `Couldn't load team (HTTP ${err.status}).`
          : err instanceof Error ? err.message : "Couldn't load team.";
        setBanner(msg, "error");
      } finally {
        if (!disposed) {
          rerenderMembers();
          rerenderMatrix();
        }
      }
    }
    void load();

    // ── Cleanup ───────────────────────────────────────────────────────────
    return (): void => {
      disposed = true;
      ac.abort();
      inviteBtn.removeEventListener("click", onInviteClick);
      modal.cancelBtn.removeEventListener("click", onCancelClick);
      modal.overlay.removeEventListener("click", onOverlayClick);
      modal.form.removeEventListener("submit", onInviteSubmit);
      document.removeEventListener("keydown", onModalKeydown);
      listEl.removeEventListener("click", onListClick);
      listEl.removeEventListener("change", onRoleChange);
      matrixEl.removeEventListener("change", onMatrixChange);
      matrixFootEl.removeEventListener("change", onMatrixFootChange);
      matrixFootEl.removeEventListener("click", onMatrixFootClick);
    };
  },
};

export default tab;
